/**
 * ThemePicker — compact light/dark control + accent swatches.
 *
 * Two controls side by side:
 *  1. A light/dark/system cycle button. Its `aria-label` intentionally contains
 *     "Basculer" and it cycles system → light → dark → system, mirroring the
 *     legacy header toggle (the live E2E suite clicks `button[aria-label*="Basculer"]`
 *     and drives the `.dark` class through it — keep both invariants).
 *  2. An accent menu: a swatch button that opens a small popover of the named
 *     accent themes wired to the UI store (persisted, applied as `data-theme`).
 *
 * Fully token-themed and keyboard accessible. Safe to mount in the dashboard
 * header (compact) or on a settings page.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { ACCENTS, ACCENT_META, useUIStore, type Accent, type Theme } from '@/stores/ui-store';
import { useT, tr } from '@/i18n';

const THEME_ORDER: Theme[] = ['system', 'light', 'dark'];
const THEME_META: Record<Theme, { label: string; icon: ReactNode }> = {
  system: {
    label: tr('shell.ui.themePicker.themeSystem'),
    icon: <path d="M4 5h16v11H4zM8 20h8M12 16v4" />,
  },
  light: {
    label: tr('shell.ui.themePicker.themeLight'),
    icon: (
      <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" />
    ),
  },
  dark: {
    label: tr('shell.ui.themePicker.themeDark'),
    icon: <path d="M20 14.5A8 8 0 0 1 9.5 4 7 7 0 1 0 20 14.5z" />,
  },
};

function ThemeCycleButton() {
  const { t } = useT();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const meta = THEME_META[theme] ?? THEME_META.system;
  const next = THEME_ORDER[(THEME_ORDER.indexOf(theme) + 1) % THEME_ORDER.length];

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      title={meta.label}
      aria-label={t('shell.ui.themePicker.labelToggle', { label: meta.label })}
      className="rounded-token p-2 text-muted transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        {meta.icon}
      </svg>
    </button>
  );
}

function AccentMenu() {
  const { t } = useT();
  const accent = useUIStore((s) => s.accent);
  const setAccent = useUIStore((s) => s.setAccent);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('shell.ui.themePicker.accentColorLabel', { label: (ACCENT_META[accent] ?? ACCENT_META.indigo).label })}
        title={t('shell.ui.themePicker.accentColor')}
        className="flex items-center rounded-token p-2 text-muted transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <span
          aria-hidden="true"
          className={`h-4 w-4 rounded-full ring-2 ring-inset ring-black/10 ${(ACCENT_META[accent] ?? ACCENT_META.indigo).swatch}`}
        />
      </button>

      {open && (
        <div
          role="group"
          aria-label={t('shell.ui.themePicker.accentColor')}
          className="absolute right-0 top-full z-50 mt-2 w-48 rounded-token-lg border border-border bg-surface p-3 shadow-elevation-3"
        >
          <div className="mb-2 px-0.5 text-xs font-semibold uppercase tracking-wide text-muted">{t('shell.ui.themePicker.accentColor')}</div>
          <div className="grid grid-cols-6 gap-2">
            {ACCENTS.map((a: Accent) => {
              const selected = a === accent;
              return (
                <button
                  key={a}
                  type="button"
                  aria-pressed={selected}
                  aria-label={ACCENT_META[a].label}
                  title={ACCENT_META[a].label}
                  onClick={() => {
                    setAccent(a);
                    setOpen(false);
                  }}
                  className={`flex h-6 w-6 items-center justify-center rounded-full ring-offset-2 ring-offset-surface transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    selected ? 'ring-2 ring-foreground' : 'hover:scale-110'
                  }`}
                >
                  <span className={`h-4 w-4 rounded-full ${ACCENT_META[a].swatch}`} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export interface ThemePickerProps {
  className?: string;
}

/** Compact light/dark toggle + accent swatch menu. */
export function ThemePicker({ className = '' }: ThemePickerProps) {
  return (
    <div className={`flex items-center gap-0.5 ${className}`.trim()}>
      <ThemeCycleButton />
      <AccentMenu />
    </div>
  );
}
